import { useEffect } from "react"
import styled from "styled-components"
import { X, Home, FilePlus, FolderOpen, User as PersonIcon, Users as TeamIcon, PanelLeftClose } from "lucide-react"

const Panel = styled.aside`
  width: 100%;
  height: 100%;
  background: #0f172a;
  color: #e2e8f0;
  border-right: 1px solid #1e293b;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 18px;
  border-bottom: 1px solid #1e293b;
`

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 800;
  color: #f8fafc;
  letter-spacing: -0.01em;

  .logo {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    color: white;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 800;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.35);
  }
`

const CloseBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  &:hover { background: #1e293b; color: #e2e8f0; }
`

const Section = styled.div`
  padding: 10px 10px;
  border-bottom: 1px solid #1e293b;
`

const SectionTitle = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding: 12px 14px 8px;
`

const NavItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: none;
  background: transparent;
  border-radius: 8px;
  text-align: left;
  font-size: 13.5px;
  font-weight: 500;
  color: #cbd5e1;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;

  & + & { margin-top: 2px; }
  svg { color: #94a3b8; flex-shrink: 0; }

  &:hover {
    background: #1e293b;
    color: #f8fafc;
    svg { color: #e2e8f0; }
  }
`

const ChartList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 4px 10px 12px;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 999px; }
`

const ChartItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid ${(p) => (p.$active ? "#1d4ed8" : "transparent")};
  background: ${(p) => (p.$active ? "rgba(37, 99, 235, 0.18)" : "transparent")};
  color: ${(p) => (p.$active ? "#f8fafc" : "#cbd5e1")};
  border-radius: 8px;
  text-align: left;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.12s, color 0.12s, border-color 0.12s;

  & + & { margin-top: 2px; }
  &:hover {
    background: ${(p) => (p.$active ? "rgba(37, 99, 235, 0.25)" : "#1e293b")};
    color: #f8fafc;
  }

  .name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: ${(p) => (p.$active ? 700 : 500)};
  }
  .badge {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 3px;
    font-size: 10.5px;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: 999px;
    background: ${(p) => (p.$team ? "rgba(59,130,246,0.18)" : "rgba(148,163,184,0.14)")};
    color: ${(p) => (p.$team ? "#93c5fd" : "#cbd5e1")};
    border: 1px solid ${(p) => (p.$team ? "rgba(59,130,246,0.35)" : "rgba(148,163,184,0.25)")};
  }
`

const Empty = styled.div`
  padding: 24px 16px;
  font-size: 12.5px;
  color: #64748b;
  text-align: center;
`

const MobileOnlyItem = styled(NavItem)`
  display: none;
  @media (max-width: 900px) {
    display: flex;
  }
`

export default function GlobalDrawer({
  open,
  onClose,
  charts,
  currentFileId,
  onNewChart,
  onGoHome,
  onSelectChart,
  onOpenLoadModal,
  onToggleSidebar,
}) {
  useEffect(() => {
    if (!open) return
    const onKey = (e) => { if (e.key === "Escape") onClose() }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [open, onClose])

  const list = Array.isArray(charts) ? charts : []

  return (
    <Panel aria-hidden={!open}>
        <Header>
          <Brand>
            <span className="logo">G</span>
            간트차트
          </Brand>
          <CloseBtn onClick={onClose} aria-label="닫기"><X size={18} /></CloseBtn>
        </Header>

        <Section>
          {onGoHome && (
            <NavItem onClick={() => { onGoHome(); onClose() }}>
              <Home size={16} /> 홈(대시보드)
            </NavItem>
          )}
          {onNewChart && (
            <NavItem onClick={() => { onNewChart(); onClose() }}>
              <FilePlus size={16} /> 새 차트
            </NavItem>
          )}
          {onOpenLoadModal && (
            <NavItem onClick={() => { onOpenLoadModal(); onClose() }}>
              <FolderOpen size={16} /> 전체 차트 관리
            </NavItem>
          )}
          {onToggleSidebar && (
            <MobileOnlyItem onClick={() => { onToggleSidebar(); onClose() }}>
              <PanelLeftClose size={16} /> 작업 사이드바 열기/닫기
            </MobileOnlyItem>
          )}
        </Section>

        <SectionTitle>내 차트</SectionTitle>
        <ChartList>
          {list.length === 0 ? (
            <Empty>저장된 차트가 없습니다.</Empty>
          ) : (
            list.map((c) => {
              const isTeam = c.chart_type === "team" || c.type === "team"
              const active = currentFileId === c.id
              return (
                <ChartItem
                  key={c.id}
                  $active={active}
                  $team={isTeam}
                  onClick={() => { onSelectChart?.(c.id); onClose() }}
                >
                  <span className="name">{c.title || c.name || `차트 ${c.id}`}</span>
                  <span className="badge">
                    {isTeam ? (<><TeamIcon size={10} /> 팀</>) : (<><PersonIcon size={10} /> 개인</>)}
                  </span>
                </ChartItem>
              )
            })
          )}
        </ChartList>
    </Panel>
  )
}
